// client/src/components/gemv2/GemV2ResultsPage.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiGetJson, buildQuery, clamp, fmtDate } from "./gemV2Api";
import Layout from '../Layout';

const COLORS = {
  text: "#1f2937",
  muted: "#6b7280",
  border: "#e5e7eb",
  border2: "#f3f4f6",
  bgHead: "#f9fafb",
  link: "#2563eb",
};

const containerStyle = { maxWidth: 1400, margin: "0 auto", padding: 16, color: COLORS.text };
const cardStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  padding: 12,
  background: "#fff",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
};
const rowStyle = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" };
const inputStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #d1d5db", minWidth: 320 };
const btnStyle = {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
  background: "#fff",
  cursor: "pointer",
  color: COLORS.text,
};
const btnActiveStyle = { ...btnStyle, border: "1px solid #111827", fontWeight: 700 };
const btnTiny = { ...btnStyle, padding: "6px 10px", borderRadius: 10 };

const smallMuted = { color: COLORS.muted, fontSize: 12 };
const tableWrap = { overflowX: "auto", borderRadius: 12, border: `1px solid ${COLORS.border}` };
const thStyle = {
  textAlign: "left",
  padding: 10,
  background: COLORS.bgHead,
  borderBottom: `1px solid ${COLORS.border}`,
  whiteSpace: "nowrap",
  verticalAlign: "top",
  lineHeight: 1.2,
};
const tdStyle = { padding: 10, borderBottom: `1px solid ${COLORS.border2}`, verticalAlign: "top" };
const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" };

const btnText = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "inherit",
  font: "inherit",
  textAlign: "left",
};

const btnLink = {
  ...btnText,
  color: COLORS.link,
  textDecoration: "underline",
  textUnderlineOffset: 2,
};

const headerFilterInputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "6px 7px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
};
const headerFilterDateStyle = { ...headerFilterInputStyle, padding: "4px 6px" };

function money(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function text(v) {
  return v == null ? "" : String(v);
}

function splitTerms(raw) {
  return String(raw || "").split(",").map((s) => s.trim()).filter(Boolean);
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function Highlight({ value, terms, filterTerm }) {
  const str = text(value);
  if (!str) return null;

  const allTerms = [
    ...(terms || []),
    ...String(filterTerm || "").split(",").map((s) => s.trim()).filter(Boolean),
  ];
  const clean = allTerms.map((t) => t.trim()).filter((t) => t.length >= 2);
  if (!clean.length) return <>{str}</>;

  const pattern = clean.map(escapeRegExp).join("|");
  let re;
  try { re = new RegExp(`(${pattern})`, "ig"); } catch { return <>{str}</>; }

  const parts = str.split(re);
  return (
    <>
      {parts.map((p, i) => {
        const isHit = clean.some((t) => p.toLowerCase() === t.toLowerCase());
        return isHit ? (
          <mark key={i} style={{ padding: "0 2px", borderRadius: 4 }}>{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        );
      })}
    </>
  );
}

function SortHeader({ label, field, sortField, sortOrder, onSort, disabled }) {
  const sortable = field && !disabled;
  const active = sortField === field;
  const arrow = sortable ? (active ? (sortOrder === "ASC" ? " \u25B2" : " \u25BC") : " \u2195") : "";

  return (
    <th
      style={{
        ...thStyle,
        cursor: sortable ? "pointer" : "default",
        opacity: disabled ? 0.6 : 1,
        fontWeight: active ? 800 : 700,
      }}
      onClick={() => sortable && onSort(field)}
      title={sortable ? "Click to sort" : ""}
    >
      <span>{label}</span>
      {sortable ? <span style={{ color: active ? COLORS.text : "#9ca3af" }}>{arrow}</span> : null}
    </th>
  );
}

function PlainHeader({ label }) {
  return (
    <th style={{ ...thStyle, cursor: "default", fontWeight: 700 }}>
      <span>{label}</span>
    </th>
  );
}

function Pagination({ page, totalPages, onPage, loading }) {
  const safeTotal = Math.max(1, totalPages || 1);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <button style={btnStyle} onClick={() => onPage(clamp(page - 1, 1, safeTotal))} disabled={page <= 1 || loading}>Prev</button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={smallMuted}>Page</span>
        <input
          style={{ ...inputStyle, minWidth: 90, width: 90 }}
          value={page}
          onChange={(e) => {
            const v = parseInt(e.target.value || "1", 10);
            onPage(clamp(Number.isNaN(v) ? 1 : v, 1, safeTotal));
          }}
        />
        <span style={smallMuted}>of {safeTotal}</span>
        {loading ? <span className="tm-spinner" title="Loading" /> : null}
      </div>
      <button style={btnStyle} onClick={() => onPage(clamp(page + 1, 1, safeTotal))} disabled={page >= safeTotal || loading}>Next</button>
    </div>
  );
}

// Column definitions per view
const SUMMARY_COLUMNS = [
  { key: "BidNumber", label: "Bid Number", kind: "text", sortField: "BidNumber", filterKey: "f_BidNumber" },
  { key: "L1SellerName", label: "L1 Seller", kind: "text", sortField: null, filterKey: "f_L1SellerName" },
  { key: "L1FinOfferedItem", label: "L1 Offered (Fin)", kind: "text", sortField: null, filterKey: "f_L1FinOfferedItem" },
  { key: "L1TotalPrice", label: "L1 Total", kind: "numeric", sortField: "L1TotalPrice", filterKey: "minL1TotalPrice" },
  { key: "UnitPrice", label: "Unit Price", kind: "numeric", sortField: "UnitPrice", filterKey: "minUnitPrice" },
  { key: "L1TechOfferedItem", label: "L1 Offered (Tech)", kind: "text", sortField: null, filterKey: "f_L1TechOfferedItem" },
  { key: "L1TechStatus", label: "Tech Status", kind: "text", sortField: null, filterKey: "f_L1TechStatus" },
  { key: "EndTS", label: "End", kind: "date", sortField: "EndTS" },
  { key: "ItemsInput", label: "Items", kind: "text", sortField: null, filterKey: "f_ItemsInput" },
  { key: "QuantityInput", label: "Qty", kind: "text", sortField: null, filterKey: "f_QuantityInput" },
  { key: "Name", label: "Name", kind: "text", sortField: null, filterKey: "f_Name" },
  { key: "Address", label: "Address", kind: "text", sortField: null, filterKey: "f_Address" },
  { key: "Ministry", label: "Ministry", kind: "text", sortField: "Ministry", filterKey: "f_Ministry" },
  { key: "Department", label: "Department", kind: "text", sortField: "Department", filterKey: "f_Department" },
  { key: "Organisation", label: "Organisation", kind: "text", sortField: null, filterKey: "f_Organisation" },
  { key: "Office", label: "Office", kind: "text", sortField: null, filterKey: "f_Office" },
];

const SELLER_COLUMNS_QUALIFIED = [
  { key: "BidNumber", label: "Bid Number", kind: "text", sortField: "BidNumber", filterKey: "f_BidNumber" },
  { key: "SellerName", label: "Seller", kind: "text", sortField: "SellerName", filterKey: "f_SellerName" },
  { key: "Status", label: "Status", kind: "text", sortField: null, filterKey: "f_Status" },
  { key: "RankNum", label: "Rank", kind: "text", sortField: "RankNum", filterKey: "f_RankNum" },
  { key: "FinTotalPrice", label: "Total Price", kind: "numeric", sortField: "FinTotalPrice", filterKey: "minFinTotalPrice" },
  { key: "UnitPrice", label: "Unit Price", kind: "numeric", sortField: "UnitPrice", filterKey: "minUnitPrice" },
  { key: "FinOfferedItem", label: "Fin Offered", kind: "text", sortField: null, filterKey: "f_FinOfferedItem" },
  { key: "TechOfferedItem", label: "Tech Offered", kind: "text", sortField: null, filterKey: "f_TechOfferedItem" },
  { key: "TechStatus", label: "Tech Status", kind: "text", sortField: null, filterKey: "f_TechStatus" },
  { key: "EndTS", label: "End", kind: "date", sortField: "EndTS" },
  { key: "ItemsInput", label: "Items", kind: "text", sortField: null, filterKey: "f_ItemsInput" },
  { key: "QuantityInput", label: "Qty", kind: "text", sortField: null, filterKey: "f_QuantityInput" },
  { key: "Ministry", label: "Ministry", kind: "text", sortField: "Ministry", filterKey: "f_Ministry" },
  { key: "Department", label: "Department", kind: "text", sortField: "Department", filterKey: "f_Department" },
];

const SELLER_COLUMNS_ALL = [
  { key: "BidNumber", label: "Bid Number", kind: "text", sortField: "BidNumber", filterKey: "f_BidNumber" },
  { key: "SellerName", label: "Seller", kind: "text", sortField: "SellerName", filterKey: "f_SellerName" },
  { key: "Status", label: "Status", kind: "text", sortField: "Status", filterKey: "f_Status" },
  { key: "RankNum", label: "Rank", kind: "text", sortField: "RankNum", filterKey: "f_RankNum" },
  { key: "FinTotalPrice", label: "Total Price", kind: "numeric", sortField: "FinTotalPrice", filterKey: "minFinTotalPrice" },
  { key: "UnitPrice", label: "Unit Price", kind: "numeric", sortField: "UnitPrice", filterKey: "minUnitPrice" },
  { key: "FinOfferedItem", label: "Fin Offered", kind: "text", sortField: null, filterKey: "f_FinOfferedItem" },
  { key: "TechOfferedItem", label: "Tech Offered", kind: "text", sortField: null, filterKey: "f_TechOfferedItem" },
  { key: "TechStatus", label: "Tech Status", kind: "text", sortField: null, filterKey: "f_TechStatus" },
  { key: "EndTS", label: "End", kind: "date", sortField: "EndTS" },
  { key: "ItemsInput", label: "Items", kind: "text", sortField: null, filterKey: "f_ItemsInput" },
  { key: "QuantityInput", label: "Qty", kind: "text", sortField: null, filterKey: "f_QuantityInput" },
  { key: "Ministry", label: "Ministry", kind: "text", sortField: "Ministry", filterKey: "f_Ministry" },
  { key: "Department", label: "Department", kind: "text", sortField: "Department", filterKey: "f_Department" },
];

const INIT_FILTERS = {
  f_BidNumber: "", f_L1SellerName: "", f_SellerName: "", f_Status: "",
  f_Ministry: "", f_Department: "", f_ItemsInput: "", f_Name: "", f_Address: "",
  f_Organisation: "", f_Office: "",
  f_L1FinOfferedItem: "", f_L1TechOfferedItem: "", f_L1TechStatus: "",
  f_QuantityInput: "", f_RankNum: "",
  f_FinOfferedItem: "", f_TechOfferedItem: "", f_TechStatus: "",
  minL1TotalPrice: "", minUnitPrice: "", minFinTotalPrice: "",
  endDateFrom: "", endDateTo: "",
};

export default function GemV2ResultsPage() {
  const [view, setView] = useState("summary");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [filters, setFilters] = useState({ ...INIT_FILTERS });
  const debouncedFilters = useDebouncedValue(filters, 350);

  // collapse bids (server-driven across pages now)
  const [collapsedBids, setCollapsedBids] = useState(() => new Set());

  // sorting
  const [sortField, setSortField] = useState("BidNumber");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resp, setResp] = useState({ total: 0, data: [], totalPages: 1, view: "" });

  const countLabel = "bids";
  const collapseResetSigRef = useRef("");

  const ALLOWED_SORT = useMemo(() => {
    if (view === "summary") return ["EndTS", "BidNumber", "Ministry", "Department", "L1TotalPrice", "UnitPrice"];
    if (view === "qualified")
      return ["EndTS", "BidNumber", "Ministry", "Department", "SellerName", "RankNum", "FinTotalPrice", "UnitPrice"];
    return ["EndTS", "BidNumber", "Ministry", "Department", "SellerName", "Status", "RankNum", "FinTotalPrice", "UnitPrice"];
  }, [view]);

  const columns = useMemo(() => {
    if (view === "summary") return SUMMARY_COLUMNS;
    if (view === "qualified") return SELLER_COLUMNS_QUALIFIED;
    return SELLER_COLUMNS_ALL;
  }, [view]);

  const endpoint = useMemo(() => {
    if (view === "summary") return "/api/gem-v2/results-v3/summary";
    if (view === "qualified") return "/api/gem-v2/results-v3/qualified";
    return "/api/gem-v2/results-v3/all";
  }, [view]);

  const effectiveSearch = useMemo(() => {
    const parts = [];
    if (debouncedSearch.trim()) parts.push(debouncedSearch.trim());
    return parts.join(", ");
  }, [debouncedSearch]);

  const isSearchMode = useMemo(() => effectiveSearch.trim().length > 0, [effectiveSearch]);

  const highlightTerms = useMemo(() => {
    const t = splitTerms(effectiveSearch);
    const seen = new Set();
    return t.filter((x) => {
      const k = x.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [effectiveSearch]);

  // reset collapses on filter/search/sort/limit/view changes
  useEffect(() => {
    const sig = JSON.stringify({
      view, effectiveSearch, debouncedFilters, sortField, sortOrder, limit,
    });
    if (collapseResetSigRef.current !== sig) {
      collapseResetSigRef.current = sig;
      setCollapsedBids(new Set());
      setPage(1);
    }
  }, [view, effectiveSearch, debouncedFilters, sortField, sortOrder, limit]);

  function onSort(field) {
    if (!ALLOWED_SORT.includes(field)) return;
    if (sortField === field) setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    else {
      setSortField(field);
      setSortOrder("DESC");
    }
    setPage(1);
  }

  function setViewAndReset(v) {
    setView(v);
    setPage(1);
    setSortField("BidNumber");
    setSortOrder("DESC");
  }

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearAllFilters() {
    setSearch("");
    setFilters({ ...INIT_FILTERS });
    setPage(1);
  }

  function toggleCollapseBid(bid) {
    if (isSearchMode) return;
    if (!bid) return;
    setCollapsedBids((prev) => {
      const next = new Set(prev);
      if (next.has(bid)) next.delete(bid);
      else next.add(bid);
      return next;
    });
  }

  const title = useMemo(() => {
    if (view === "summary") return "GeM Results \u2014 Summary (L1 only)";
    if (view === "qualified") return "GeM Results \u2014 Qualified Sellers";
    return "GeM Results \u2014 All Sellers (incl. disqualified)";
  }, [view]);

  // base params for server
  const baseParams = useMemo(() => {
    const p = {
      search: effectiveSearch.trim(),
      sortField,
      sortOrder,
    };
    // Column filters
    Object.entries(debouncedFilters).forEach(([k, v]) => {
      if (v) p[k] = v;
    });
    return p;
  }, [effectiveSearch, sortField, sortOrder, debouncedFilters]);

  // fetch
  useEffect(() => {
    let mounted = true;

    async function run() {
      try {
        setLoading(true);
        setError("");

        const q = buildQuery({ ...baseParams, page, limit });
        const json = await apiGetJson(`${endpoint}${q}`, 'results');

        if (!mounted) return;
        setResp(json);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || String(e));
        setResp({ total: 0, data: [], totalPages: 1, view });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    run();
    return () => { mounted = false; };
  }, [endpoint, baseParams, page, limit]);

  // counts + first row index per BidNumber (for client-side collapse)
  const bidRowCounts = useMemo(() => {
    const m = new Map();
    for (const r of resp?.data || []) {
      const b = String(r?.BidNumber || "");
      if (!b) continue;
      m.set(b, (m.get(b) || 0) + 1);
    }
    return m;
  }, [resp]);

  const firstRowIndexByBid = useMemo(() => {
    const m = new Map();
    (resp?.data || []).forEach((r, idx) => {
      const b = String(r?.BidNumber || "");
      if (!b) return;
      if (!m.has(b)) m.set(b, idx);
    });
    return m;
  }, [resp]);

  const totalBids = useMemo(() => {
    const t = Number(resp?.total);
    return Number.isFinite(t) ? t : 0;
  }, [resp]);

  // Helper to get filter term for a column (for highlight)
  function getFilterTerm(col) {
    if (!col.filterKey) return "";
    return debouncedFilters[col.filterKey] || "";
  }

  return (
  <Layout>
    <div style={containerStyle}>
      <style>{`
        .tm-loadingbar-wrap{
          height: 3px; border-radius: 999px; background: #eef2ff;
          overflow: hidden; margin-top: 10px;
        }
        .tm-loadingbar{
          height: 100%; width: 40%; background: #111827;
          transform: translateX(-120%); animation: tmbar 1.1s infinite;
        }
        @keyframes tmbar{
          0%{ transform: translateX(-120%); opacity: .35; }
          50%{ opacity: .9; }
          100%{ transform: translateX(260%); opacity: .35; }
        }
        .tm-spinner{
          width: 14px; height: 14px; border-radius: 999px;
          border: 2px solid #e5e7eb; border-top-color: #111827;
          animation: tmspin .8s linear infinite; display: inline-block;
        }
        @keyframes tmspin{
          from{ transform: rotate(0deg); } to{ transform: rotate(360deg); }
        }
      `}</style>

      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, display: "flex", gap: 10, alignItems: "center" }}>
              <span>{title}</span>
              {loading ? <span className="tm-spinner" title="Loading" /> : null}
            </div>
            {view !== "summary" && isSearchMode ? (
              <div style={{ marginTop: 6, ...smallMuted }}>Search mode: collapse disabled</div>
            ) : null}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 800 }}>
              {Number(totalBids || 0).toLocaleString()} {countLabel}
            </div>
            <div style={smallMuted}>view = {view}</div>
          </div>
        </div>

        {loading ? (
          <div className="tm-loadingbar-wrap" title="Loading"><div className="tm-loadingbar" /></div>
        ) : null}

        <div style={{ marginTop: 12, ...rowStyle }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={view === "summary" ? btnActiveStyle : btnStyle} onClick={() => setViewAndReset("summary")}>Summary</button>
            <button style={view === "qualified" ? btnActiveStyle : btnStyle} onClick={() => setViewAndReset("qualified")}>Qualified</button>
            <button style={view === "all" ? btnActiveStyle : btnStyle} onClick={() => setViewAndReset("all")}>All</button>
          </div>

          <input
            style={inputStyle}
            placeholder="Search (comma-separated): e.g. vip,delhi,l1 seller"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />

          <button style={btnStyle} onClick={clearAllFilters} disabled={loading}>
            Clear Filters
          </button>

          <div style={{ marginLeft: "auto" }}>
            <Pagination page={page} totalPages={resp.totalPages} onPage={setPage} loading={loading} />
          </div>
        </div>

        {error ? (
          <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>Error: {error}</div>
        ) : null}
      </div>

      <div style={tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {columns.map((col) => (
                col.sortField ? (
                  <SortHeader key={col.key} label={col.label} field={col.sortField} sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
                ) : (
                  <PlainHeader key={col.key} label={col.label} />
                )
              ))}
            </tr>
            <tr>
              {columns.map((col) => {
                const baseTh = { ...thStyle, background: "#fff", padding: 6, whiteSpace: "normal" };

                if (col.kind === "date") {
                  return (
                    <th key={`${col.key}_filter`} style={baseTh}>
                      <div style={{ display: "grid", gap: 4 }}>
                        <input type="date" style={headerFilterDateStyle} value={filters.endDateFrom} onChange={(e) => setFilter("endDateFrom", e.target.value)} title="From date" />
                        <input type="date" style={headerFilterDateStyle} value={filters.endDateTo} onChange={(e) => setFilter("endDateTo", e.target.value)} title="To date" />
                      </div>
                    </th>
                  );
                }

                if (col.kind === "numeric" && col.filterKey) {
                  return (
                    <th key={`${col.key}_filter`} style={baseTh}>
                      <input
                        type="number"
                        step="0.01"
                        style={headerFilterInputStyle}
                        placeholder="Min"
                        value={filters[col.filterKey] || ""}
                        onChange={(e) => setFilter(col.filterKey, e.target.value)}
                      />
                    </th>
                  );
                }

                if (col.kind === "text" && col.filterKey) {
                  return (
                    <th key={`${col.key}_filter`} style={baseTh}>
                      <input
                        style={headerFilterInputStyle}
                        placeholder="Filter"
                        value={filters[col.filterKey] || ""}
                        onChange={(e) => setFilter(col.filterKey, e.target.value)}
                      />
                    </th>
                  );
                }

                // No filter for this column
                return <th key={`${col.key}_filter`} style={baseTh} />;
              })}
            </tr>
          </thead>

          <tbody>
            {(resp.data || []).map((r, idx) => {
              const bid = text(r?.BidNumber);
              const isCollapsedHeader = Boolean(r?.IsCollapsedHeader);

              if (view === "summary") {
                return (
                  <tr key={`${bid || "x"}_${idx}`}>
                    <td style={{ ...tdStyle, ...mono }}>
                      <Highlight value={bid || "-"} terms={highlightTerms} filterTerm={getFilterTerm(columns[0])} />
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 220 }}>
                      <Highlight value={r.L1SellerName} terms={highlightTerms} filterTerm={debouncedFilters.f_L1SellerName} />
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 240 }}>
                      <Highlight value={r.L1FinOfferedItem} terms={highlightTerms} filterTerm={debouncedFilters.f_L1FinOfferedItem} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{money(r.L1TotalPrice)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{money(r.UnitPrice)}</td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 240 }}>
                      <Highlight value={r.L1TechOfferedItem} terms={highlightTerms} filterTerm={debouncedFilters.f_L1TechOfferedItem} />
                    </td>
                    <td style={tdStyle}><Highlight value={r.L1TechStatus} terms={highlightTerms} filterTerm={debouncedFilters.f_L1TechStatus} /></td>
                    <td style={tdStyle}>{fmtDate(r.EndTS) || text(r.EndDateInput)}</td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 240 }}>
                      <Highlight value={r.ItemsInput} terms={highlightTerms} filterTerm={debouncedFilters.f_ItemsInput} />
                    </td>
                    <td style={tdStyle}><Highlight value={r.QuantityInput} terms={highlightTerms} filterTerm={debouncedFilters.f_QuantityInput} /></td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 160 }}>
                      <Highlight value={r.Name} terms={highlightTerms} filterTerm={debouncedFilters.f_Name} />
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 220 }}>
                      <Highlight value={r.Address} terms={highlightTerms} filterTerm={debouncedFilters.f_Address} />
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap" }}>
                      <Highlight value={r.Ministry} terms={highlightTerms} filterTerm={debouncedFilters.f_Ministry} />
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap" }}>
                      <Highlight value={r.Department} terms={highlightTerms} filterTerm={debouncedFilters.f_Department} />
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap" }}>
                      <Highlight value={r.Organisation} terms={highlightTerms} filterTerm={debouncedFilters.f_Organisation} />
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "pre-wrap" }}>
                      <Highlight value={r.Office} terms={highlightTerms} filterTerm={debouncedFilters.f_Office} />
                    </td>
                  </tr>
                );
              }

              // qualified / all rows
              const seller = text(r.SellerName);
              const statusShown = text(r.Status || r.TechStatus || "");

              const bidRowsCount = bidRowCounts.get(bid) || 0;
              const firstIdx = firstRowIndexByBid.get(bid);
              if (!isSearchMode && collapsedBids.has(bid) && firstIdx != null && idx !== firstIdx) {
                return null;
              }

              const rowBg = "#fff";

              return (
                <tr key={`${bid || "x"}_${idx}`}>
                  <td style={{ ...tdStyle, ...mono, background: rowBg }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {!isSearchMode ? (
                        <button
                          style={{ ...btnText, fontWeight: 900, color: COLORS.muted, width: 18 }}
                          onClick={() => toggleCollapseBid(bid)}
                          title={collapsedBids.has(bid) ? "Expand this bid" : "Collapse this bid"}
                        >
                          {collapsedBids.has(bid) ? "\u25B8" : "\u25BE"}
                        </button>
                      ) : (
                        <span style={{ width: 18 }} />
                      )}
                      <button style={btnTiny} onClick={() => { setFilter("f_BidNumber", bid); setPage(1); }} title="Filter by this bid">
                        <Highlight value={bid || "-"} terms={highlightTerms} filterTerm={debouncedFilters.f_BidNumber} />
                      </button>
                      {bidRowsCount ? (
                        <span style={smallMuted} title="Total rows for this bid">({Number(bidRowsCount).toLocaleString()})</span>
                      ) : null}
                      {isCollapsedHeader ? <span style={{ ...smallMuted, marginLeft: 6 }}>collapsed</span> : null}
                    </div>
                  </td>

                  <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 220, background: rowBg }}>
                    <Highlight value={seller} terms={highlightTerms} filterTerm={debouncedFilters.f_SellerName} />
                  </td>

                  <td style={{ ...tdStyle, background: rowBg }}>
                    <Highlight value={statusShown} terms={highlightTerms} filterTerm={debouncedFilters.f_Status} />
                  </td>
                  <td style={{ ...tdStyle, background: rowBg }}>
                    <Highlight value={text(r.RankNum || r.FinRank || "")} terms={highlightTerms} filterTerm={debouncedFilters.f_RankNum} />
                  </td>
                  <td style={{ ...tdStyle, background: rowBg, textAlign: "right" }}>{money(r.FinTotalPrice)}</td>
                  <td style={{ ...tdStyle, background: rowBg, textAlign: "right" }}>{money(r.UnitPrice)}</td>

                  <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 240, background: rowBg }}>
                    <Highlight value={r.FinOfferedItem} terms={highlightTerms} filterTerm={debouncedFilters.f_FinOfferedItem} />
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 240, background: rowBg }}>
                    <Highlight value={r.TechOfferedItem} terms={highlightTerms} filterTerm={debouncedFilters.f_TechOfferedItem} />
                  </td>
                  <td style={{ ...tdStyle, background: rowBg }}><Highlight value={r.TechStatus} terms={highlightTerms} filterTerm={debouncedFilters.f_TechStatus} /></td>
                  <td style={{ ...tdStyle, background: rowBg }}>{fmtDate(r.EndTS) || text(r.EndDateInput)}</td>

                  <td style={{ ...tdStyle, whiteSpace: "pre-wrap", minWidth: 240, background: rowBg }}>
                    <Highlight value={r.ItemsInput} terms={highlightTerms} filterTerm={debouncedFilters.f_ItemsInput} />
                  </td>
                  <td style={{ ...tdStyle, background: rowBg }}><Highlight value={r.QuantityInput} terms={highlightTerms} filterTerm={debouncedFilters.f_QuantityInput} /></td>

                  <td style={{ ...tdStyle, whiteSpace: "pre-wrap", background: rowBg }}>
                    <Highlight value={r.Ministry} terms={highlightTerms} filterTerm={debouncedFilters.f_Ministry} />
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "pre-wrap", background: rowBg }}>
                    <Highlight value={r.Department} terms={highlightTerms} filterTerm={debouncedFilters.f_Department} />
                  </td>
                </tr>
              );
            })}

            {!loading && (!resp.data || resp.data.length === 0) ? (
              <tr>
                <td style={{ ...tdStyle, color: COLORS.muted }} colSpan={columns.length}>
                  No data found for current filters.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div style={{ ...cardStyle, marginTop: 12 }}>
        <Pagination page={page} totalPages={resp.totalPages} onPage={setPage} loading={loading} />
      </div>
    </div>
  </Layout>
  );
}
